import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import AgendaUtils from "../../utils/Agenda";
import { Typography, TextField, Button, CircularProgress, Alert, Snackbar, MenuItem, Stepper, Step, StepLabel, Box, Card, CardContent, Grid, FormControlLabel, Checkbox } from "@mui/material";
import IUser from "../../types/User";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import AuthService from "../../services/AuthService";
import * as constantes from '../../common/Constantes';
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import { createTheme } from '@mui/material/styles';
import IAlert from "../../types/Alert";
import Utilities from "../../common/Utilities";
import CheckOut from "./CheckOut";
import { Elements } from "@stripe/react-stripe-js";
import stripePromise, { stripeElementsOptions } from "../../common/Stripe";
import StripeService from "../../services/StripeService";
import { styled } from '@mui/system';
import PaymentFailed from "./PaymentFailed";
import { IPlan } from "../../types/Plan";
import PasswordField from "../utilities/PasswordField";
import { trackCompleteRegistration, trackRegistrationProgress, trackSelectItem, setGtagUserId } from "../../utils/TrackingEvents";

export type SignUpForm = Partial<IUser> & {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    passwordConfirm: string;

    clinicName?: string;
    clinicCity?: string;
    clinicDomicilio?: string;
    clinicEmail?: string;
    clinicTelefono?: string;
    clinicProvincia?: string;

    stripeCustomerId?: string;
    _id?: string;

    datosFacturacion?: {
        nombre?: string;
        nif?: string;
        calle?: string;
        ciudad?: string;
        codigoPostal?: string;
        pais?: string;
    };

    subscription?: {
        planId?: string;
        status?: string;
    };
};

const allowedTesterEmails = import.meta.env.REACT_APP_ALLOWED_TESTER_EMAILS?.split(',') || [];
const steps = ['Datos personales', 'Verificación de correo', 'Datos de la clínica', 'Datos de facturación', 'Selección del plan', 'Datos de pago'];
export const planOrder: Record<string, number> = { Tester: 1, Basic: 2, Pro: 3 };

const SignUp = ({ userId }: { userId: string | null }) => {
    const theme = createTheme();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    
    const [clinicProvincia, setProvincia] = useState(constantes.defaultProvincia);
    const [country, setCountry] = useState(constantes.paises[0].id);
    const [selectedPlan, setSelectedPlan] = useState('');

    const [activeStep, setActiveStep] = useState(0);

    const provincias = useMemo(() => AgendaUtils.getProvincias(country), [country]);

    const stepSchemas: yup.ObjectSchema<any>[] = useMemo(() => [
        yup.object({
            email: yup.string().required('El correo electrónico es obligatorio').email('Debe ser un correo electrónico válido'),
            firstName: yup.string().required('El nombre es obligatorio'),
            lastName: yup.string().required('Los apellidos son obligatorios'),
            password: yup.string().required('La contraseña es obligatoria').min(8, 'La contraseña debe tener al menos 8 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/, 'Debe contener una mayúscula, una minúscula, un número y un carácter especial'),
            passwordConfirm: yup.string().required('La confirmación de contraseña es obligatoria').oneOf([yup.ref('password')], 'Las contraseñas deben coincidir')
        }),
        yup.object({
            email: yup.string().required('El correo electrónico es obligatorio').email('Debe ser un correo electrónico válido'),
        }),
        yup.object({
            clinicName: yup.string().required('El nombre de la clínica es obligatorio'),
            clinicCity: yup.string().required('La ciudad de la clínica es obligatoria'),
            clinicDomicilio: yup.string().required('El domicilio de la clínica es obligatorio'),
            clinicEmail: yup.string().required('El email de la clínica es obligatorio').email('Debe ser un correo electrónico válido'),
            clinicTelefono: yup.string().required('El teléfono de la clínica es obligatorio').test('telefono-validation', 'El teléfono no tiene un formato válido', (value) => {
                if (!value) return false;
                if (country === 'ES') return /^\d{9}$/.test(value);
                // Fuera de España: se acepta el prefijo explícito (+...) o, si no se indica, se asume el país seleccionado.
                return !!parsePhoneNumberFromString(value, country as CountryCode)?.isValid();
            }),
        }),
        yup.object({
            datosFacturacion: yup.object({
                nombre: yup.string().required('El nombre o razón social es obligatorio'),
                nif: yup.string().test('nif-validation', 'El NIF no es válido', function (value) {
                    const pais = this.parent.pais || 'ES';
                    if (Utilities.validateTaxId(value || '', pais)) return true;
                    return this.createError({ message: `${Utilities.getNifLabel(pais)} no válido` });
                  }).required('El NIF es obligatorio'),
                calle: yup.string().required('La calle es obligatoria'),
                ciudad: yup.string().required('La ciudad es obligatoria'),
                codigoPostal: yup.string().test('cp-validation', 'El código postal no tiene un formato válido', function (value) {
                    return Utilities.validatePostalCode(value || '', this.parent.pais || 'ES');
                  }).required('El código postal es obligatorio')
            })
        }),
       yup.object({
            subscription: yup.object({
                planId: yup.string().required('Seleccionar un plan es obligatorio')
            })
       })
    ], [country]);

    const {register, setValue, getValues, handleSubmit, trigger, clearErrors, formState: { errors }} = useForm<SignUpForm>({ resolver: yupResolver(stepSchemas[activeStep]) as any, defaultValues: { clinicProvincia: constantes.defaultProvincia, datosFacturacion: { pais: constantes.paises[0].id } } });

    // Si el país seleccionado no tiene datos de provincia (el selector no se muestra), no debe
    // quedarse guardada la provincia por defecto de España — se envía vacía en su lugar.
    useEffect(() => {
        if (provincias.length === 0 && clinicProvincia !== '') {
            setProvincia('');
            setValue('clinicProvincia', '');
        }
    }, [provincias, clinicProvincia, setValue]);
    const [registrationUser, setRegistrationUser] = useState<IUser>();
    const [accepted, setAccepted] = useState(false);
    const [modifyEmail, setModifyEmail] = useState(false);

    const [timer, setTimer] = useState(0);
    const [disabled, setDisabled] = useState(false);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [plans, setPlans] = useState<IPlan[]>([]);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(interval);
        } else {
            setDisabled(false);
        }
    }, [timer]);

    useEffect(() => {
        const isUserId = !!userId && !userId.includes('@');
        if (isUserId) {
            AuthService.getUser(userId!)
            .then((response) => {
                setRegistrationUser(response.data);
                setGtagUserId(response.data._id);
                setValue("email", response.data.email);
                setValue("_id", response.data._id);
                setValue("stripeCustomerId", response.data.stripeCustomerId);
                if (response.data.subscription) {
                    setValue("subscription.planId", response.data.subscription.planId);
                }
                if (response.data === null) return;

                if (!response.data.verifiedEmail) {
                    setAlert({ open: false, severity: 'warning', msg: 'Es necesario verificar el correo electrónico' });
                    setActiveStep(1);
                }
                else if (response.data.verifiedEmail && response.data.clinicName === undefined) {
                    setAlert({ open: true, severity: 'warning', msg: 'Faltan datos necesarios para poder acceder' });
                    setActiveStep(2);
                    trackCompleteRegistration();
                }
                else if (response.data.verifiedEmail && response.data.clinicName && response.data.datosFacturacion === undefined) {
                    setAlert({ open: true, severity: 'warning', msg: 'Faltan datos necesarios para poder acceder' });
                    setActiveStep(3);
                }
                else if (response.data.verifiedEmail && response.data.clinicName && response.data.datosFacturacion && response.data.stripeCustomerId === undefined) {
                    setActiveStep(4);
                }
                else if (response.data.verifiedEmail && response.data.clinicName && response.data.datosFacturacion && response.data.stripeCustomerId && response.data.subscription === undefined) {
                    setAlert({ open: true, severity: 'success', msg: '¡Bienvenido de nuevo! Completa tus datos para poder acceder' });
                    setValue("clinicName", response.data.clinicName);
                    setValue("clinicCity", response.data.clinicCity);
                    setValue("clinicDomicilio", response.data.clinicDomicilio);
                    setValue("clinicEmail", response.data.clinicEmail);
                    setValue("clinicTelefono", response.data.clinicTelefono);
                    setValue("datosFacturacion.nombre", response.data.datosFacturacion.nombre);
                    setValue("datosFacturacion.nif", response.data.datosFacturacion.nif);
                    setValue("datosFacturacion.calle", response.data.datosFacturacion.calle);
                    setValue("datosFacturacion.ciudad", response.data.datosFacturacion.ciudad);
                    setValue("datosFacturacion.codigoPostal", response.data.datosFacturacion.codigoPostal);
                    setValue("datosFacturacion.pais", response.data.datosFacturacion.pais);
                    setCountry(response.data.datosFacturacion.pais);
                    setActiveStep(2);
                }
                else if (response.data.verifiedEmail && response.data.clinicName && response.data.datosFacturacion && response.data.stripeCustomerId && response.data.subscription.status !== 'active') {
                    switch (response.data.subscription.status) {
                        case 'pending':
                            setAlert({ open: true, severity: 'warning', msg: 'La suscripción está pendiente del pago' });
                            break;
                        case 'payment_failed':
                            setAlert({ open: true, severity: 'warning', msg: 'Ha fallado el pago de la suscripción. Actualiza los datos de pago' });
                            break;
                    }
                    setActiveStep(5);
                }
            })
            
        } else {
            setValue("email", userId || '');
        }

    }, []);

    useEffect(() => {
        if (activeStep === 4) {
            const getPlans = async () => {
                try {
                    const response = await StripeService.getAvailablePlans();
                    setPlans(response.data);
                    setLoadingPlans(false);
                } catch (error: any) {
                    setAlert({ open: true, severity: 'error', msg: error.message });
                }
            }
            getPlans();
        }
    }, [activeStep]);

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const saveUser = (data: SignUpForm) => {
        data.role = constantes.ROLES.ADMINISTRADOR;
        AuthService.signUp(data)
        .then((response: any) => {
            setDisabled(true);
            setTimer(30);
            setActiveStep(1);
            setRegistrationUser(response.data);
            setGtagUserId(response.data._id);
            trackRegistrationProgress('account_created');
            setAlert({ open: false, severity: 'success', msg: `Se ha enviado un email a ${data.email} con el enlace para verificar el correo` });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.saveUser', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const editEmail = (id: string, email: string) => {
        AuthService.editEmail(id, { email })
        .then((response: any) => {
            setModifyEmail(false);
            setDisabled(true);
            setTimer(30);
            setAlert({ open: false, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.editEmail', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: false, severity: 'error', msg: e.response.data.message });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const saveClinicData = (id: string, data: SignUpForm) => {
        AuthService.saveClinicData(id, data)
        .then((response: any) => {
            setActiveStep(3);
            trackRegistrationProgress('clinic_data');
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.saveClinicData', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const saveDatosFacturacion = (id: string, data: SignUpForm) => {
        AuthService.saveDatosFacturacion(id, data)
        .then((response: any) => {
            setActiveStep(4);
            trackRegistrationProgress('billing_data');
            setAlert({ open: true, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.saveDatosFacturacion', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const createClienteStripe = (id: string, data: SignUpForm) => {
        AuthService.createClienteStripe(id, data)
        .then((response: any) => {
            setValue("stripeCustomerId", response.data.stripeCustomerId);
            setValue("subscription.planId", response.data.planId);
            setActiveStep(5);
            setAlert({ open: true, severity: 'success', msg: "Plan seleccionado correctamente" });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.createClienteStripe', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const handleNext = async (data: SignUpForm) => {
        const isValid = await trigger(); // Valida los campos del paso actual
        if (!isValid) return;
    
        if (activeStep === 0) {
            if (!accepted) {
                setAlert({ open: true, severity: 'warning', msg: "Debes aceptar los términos y condiciones y la política de privacidad para continuar." });
                return;
            }
            setLoading(true);
            saveUser(data);
        } else if (activeStep === 1) {
            if (!data._id) return;
            if (data.email === registrationUser?.email) {
                setAlert({ open: false, severity: 'error', msg: "El correo electrónico es el mismo" });
                return;
            }
            setLoading(true);
            editEmail(data._id, data.email);
        } else if (activeStep === 2) {
            if (!data._id) return;
            setLoading(true);
            // Fuera de España se acepta cualquier formato (prefijo, espacios...), pero se guarda
            // normalizado a solo dígitos locales, igual que ya se hace siempre para España.
            if (country !== 'ES' && data.clinicTelefono) {
                const parsed = parsePhoneNumberFromString(data.clinicTelefono, country as CountryCode);
                if (parsed?.isValid()) {
                    data.clinicTelefono = parsed.nationalNumber;
                }
            }
            saveClinicData(data._id, data);
        } else if (activeStep === 3) {
            if (!data._id) return;
            setLoading(true);
            saveDatosFacturacion(data._id, data);
        } else if (activeStep === 4) {
            if (!data._id) return;
            setLoading(true);
            createClienteStripe(data._id, data);
        }
    };

    const handleReSendEmail = () => {
        if (!registrationUser || !registrationUser._id) return;
        setLoading(true);
        setDisabled(true);
        setTimer(30);

        AuthService.reSendEmail(registrationUser._id)
        .then((response: any) => {
            setAlert({ open: false, severity: 'success', msg: response.data.message });
        })
        .catch((e: any) => {
            const error: ILog = { method: 'SignUp.Component.handleReSendEmail', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: false, severity: 'error', msg: e.response.data.message });
            setDisabled(false);
            setTimer(0);
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const ResponsiveStepper = styled(Stepper)(({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          '.MuiStepLabel-label': {
            display: 'none',
          },
          '.MuiStep-root': {
            padding: theme.spacing(0.5),
          },
        },
    }));

    try {
        return (
            <>
            <Typography variant="h4" sx={{ textAlign: 'center', margin: theme.spacing(1, 0, 1) }}>{registrationUser?.subscription?.status !== 'payment_failed' ? 'Registrarse' : 'Actualiza tu método de pago' }</Typography>
            { registrationUser?.subscription?.status === 'payment_failed' && 
            <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No hemos podido procesar el último pago de tu suscripción.
                </Typography> 
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Por favor, actualiza tu método de pago para mantener el acceso a tu cuenta.
                </Typography>
            </>
            }
                {registrationUser?.subscription?.status !== 'payment_failed' && 
                <ResponsiveStepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </ResponsiveStepper>}
                <form onSubmit={handleSubmit(handleNext)} noValidate>
                {
                    activeStep === 0 &&
                    <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                        <TextField {...register("email")} variant="outlined" label="Email" margin="normal" fullWidth required autoFocus helperText={errors.email?.message} error={!!errors.email?.message} />
                        <TextField {...register("firstName")} variant="outlined" label="Nombre" margin="normal" fullWidth required helperText={errors.firstName?.message} error={!!errors.firstName?.message} />
                        <TextField {...register("lastName")} variant="outlined" label="Apellidos" margin="normal" fullWidth required helperText={errors.lastName?.message} error={!!errors.lastName?.message} />
                        <PasswordField<SignUpForm>
                          register={register}
                          errors={errors}
                          name="password"
                          label="Contraseña"
                        />
                                        
                        <PasswordField<SignUpForm>
                          register={register}
                          errors={errors}
                          name="passwordConfirm"
                          label="Confirmar contraseña"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={accepted}
                              onChange={(e) => setAccepted(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <span style={{ fontSize: "0.85rem" }}>
                              Acepto los{" "}
                              <a href="https://zyrapp.com/terms-conditions" target="_blank" rel="noopener noreferrer" style={{ fontWeight: "bold", textDecoration: "none", color: "inherit" }}>
                                Términos y condiciones
                              </a>{" "}
                              y la{" "}
                              <a href="https://zyrapp.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ fontWeight: "bold", textDecoration: "none", color: "inherit" }}>
                                Política de privacidad
                              </a>.
                            </span>
                          }
                        />
                    </Box>
                }
                {
                    activeStep === 1 &&
                    <>
                        <Alert variant="filled" severity={alert.severity} sx={{ width: '100%', mt: 1 }}>{alert.msg}</Alert>
                        <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
                            <Button disabled={disabled || loading} onClick={handleReSendEmail}>
                            {loading ? 'Reenviando...' : disabled ? `Espera ${timer} s` : 'Reenviar correo de verificación'}
                            </Button>
                            <Button onClick={() => setModifyEmail(!modifyEmail)}>Modificar correo</Button>
                        </Box>
                        {modifyEmail && <TextField {...register("email")} variant="outlined" label="Nuevo email" margin="normal" fullWidth required autoFocus helperText={errors.email?.message} error={!!errors.email?.message} />}
                    </>
                }
                {
                    activeStep === 2 &&
                    <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                        <TextField {...register("clinicName")} variant="outlined" label="Nombre de la clínica" margin="normal" fullWidth required autoFocus helperText={errors.clinicName?.message} error={!!errors.clinicName?.message} />
                        <TextField value={country} select variant="outlined" label="País" margin="normal" fullWidth required onChange={(e) => {setCountry(e.target.value), setValue("datosFacturacion.pais", e.target.value)}} >
                            {
                                constantes.paises.map((pais: any) => {
                                    return (
                                        <MenuItem key={pais.id} value={pais.id}>{pais.name}</MenuItem>
                                    );
                                })
                            }
                        </TextField>
                        {provincias.length > 0 &&
                        <TextField value={clinicProvincia} select variant="outlined" label="Provincia de la clínica" margin="normal" fullWidth required onChange={(e) => {setProvincia(e.target.value), setValue("clinicProvincia", e.target.value)}} >
                            {
                                provincias.map((provincia) => {
                                    return (
                                        <MenuItem key={provincia.id} value={provincia.id}>{provincia.name}</MenuItem>
                                    );
                                })
                            }
                        </TextField>}
                        <TextField {...register("clinicCity")} variant="outlined" label="Ciudad de la clínica" margin="normal" fullWidth required helperText={errors.clinicCity?.message} error={!!errors.clinicCity?.message} />
                        <TextField {...register("clinicDomicilio")} variant="outlined" label="Domicilio de la clínica" margin="normal" fullWidth required helperText={errors.clinicDomicilio?.message} error={!!errors.clinicDomicilio?.message} />
                        <TextField {...register("clinicEmail")} variant="outlined" label="Email de la clínica" margin="normal" fullWidth required helperText={errors.clinicEmail?.message} error={!!errors.clinicEmail?.message} />
                        <TextField {...register("clinicTelefono")} variant="outlined" label="Teléfono de la clínica" margin="normal" fullWidth required helperText={errors.clinicTelefono?.message || (country !== 'ES' ? 'Indica el prefijo (+...) o se asumirá el de tu país' : undefined)} error={!!errors.clinicTelefono?.message} />
                    </Box>
                }
                {
                    activeStep === 3 &&
                    <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto' }}>
                        <TextField {...register("datosFacturacion.nombre")} variant="outlined" label="Nombre y apellidos o Razón social" margin="normal" fullWidth required autoFocus helperText={errors.datosFacturacion?.nombre?.message} error={!!errors.datosFacturacion?.nombre?.message} />
                        <TextField {...register("datosFacturacion.nif")} variant="outlined" label={Utilities.getNifLabel(country)} margin="normal" fullWidth required helperText={errors.datosFacturacion?.nif?.message} error={!!errors.datosFacturacion?.nif?.message} />
                        <TextField {...register("datosFacturacion.calle")} variant="outlined" label="Calle" margin="normal" fullWidth required helperText={errors.datosFacturacion?.calle?.message} error={!!errors.datosFacturacion?.calle?.message} />
                        <TextField {...register("datosFacturacion.ciudad")} variant="outlined" label="Ciudad" margin="normal" fullWidth required helperText={errors.datosFacturacion?.ciudad?.message} error={!!errors.datosFacturacion?.ciudad?.message} />
                        <TextField {...register("datosFacturacion.codigoPostal")} variant="outlined" label="Código postal" margin="normal" fullWidth required helperText={errors.datosFacturacion?.codigoPostal?.message} error={!!errors.datosFacturacion?.codigoPostal?.message} />
                    </Box>
                }
                {
                    activeStep === 4 && (
                        loadingPlans ? (
                            <CircularProgress />
                        ) : (
                            <Box sx={{ width: '100%', mx: 'auto', mt: theme.spacing(2) }}>
                                <Grid container spacing={4}>
                                    {plans
                                        .sort((a: IPlan, b: IPlan) => {
                                          if (a.amount !== b.amount) {
                                            return a.amount - b.amount;
                                          }
                                          const aType = a.name.split(' ').pop()!;
                                          const bType = b.name.split(' ').pop()!;
                                          return (planOrder[aType] || 99) - (planOrder[bType] || 99);
                                        })
                                        .map((plan) => {
                                        const userEmail = getValues('email');
                                        const isTesterEligible = allowedTesterEmails.includes(userEmail);
                                        if (plan.name.includes('Tester') && !isTesterEligible) {
                                            return null;
                                        }
                                    
                                        return (
                                            <Grid item xs={12} md={4} key={plan.priceId}>
                                              <Card
                                                sx={{
                                                  height: '100%',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  cursor: 'pointer',
                                                  boxShadow: 3,
                                                  transition: 'transform 0.3s ease',
                                                  '&:hover': { transform: 'scale(1.05)' },
                                                  border: selectedPlan === plan.priceId ? '2px solid black' : 'none',
                                                }}
                                                onClick={() => {
                                                  setSelectedPlan(plan.priceId);
                                                  setValue('subscription.planId', plan.priceId);
                                                  clearErrors('subscription.planId');
                                                  trackSelectItem({
                                                    value: plan.amount,
                                                    currency: plan.currency,
                                                    items: [{ item_id: plan.priceId, item_name: plan.name, price: plan.amount }],
                                                  });
                                                }}
                                              >
                                                <CardContent>
                                                  <Typography variant="h5" component="div">
                                                    {plan.name}
                                                  </Typography>
                                                  <Typography variant="h6" sx={{ mt: 2 }}>
                                                    {plan.price}
                                                  </Typography>
                                                  {plan.limit !== null && (
                                                    <Typography variant="body2" color="text.secondary">
                                                      Hasta {plan.limit} usuarios activos
                                                    </Typography>
                                                  )}
                      
                                                  {plan.limit === null && (
                                                    <Typography variant="body2" color="text.secondary">
                                                      Sin límite de usuarios
                                                    </Typography>
                                                  )}
                                                </CardContent>
                                              </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                                {errors.subscription?.planId && (
                                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                        {errors.subscription.planId.message}
                                    </Typography>
                                )}
                            </Box>
                        )
                    )
                }

                {
                    activeStep === 5 &&
                      <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      {(() => {
                        const planId = getValues('subscription.planId');
                        const userId = getValues('_id');
                        if (registrationUser?.subscription?.status === 'payment_failed') {
                            return (
                                <PaymentFailed user={registrationUser}/>
                            );
                        } else {
                            return (
                                <CheckOut 
                                    userId={userId!}
                                    planId={planId!}
                                />
                            );
                        }
                      })()}
                      </Elements>
                }
                <Box display="flex" justifyContent="space-between" mt={2} mb={2}>
                 {(() => {
                    if (activeStep === 0 || activeStep === 2 || activeStep === 3) {
                      // Paso 0, Paso 2 y Paso 3: Solo botón de continuar
                      return (
                        <Button variant="contained" color="primary" type="submit">
                          {loading ? <CircularProgress size={25} /> : 'Continuar'}
                        </Button>
                      );
                    } else if (activeStep === 1 && modifyEmail) {
                      // Paso 1: Solo botón de continuar si modifyEmail es true
                      return (
                        <Button variant="contained" color="primary" type="submit">
                          {loading ? <CircularProgress size={25} /> : 'Continuar'}
                        </Button>
                      );
                    } else if (activeStep === 4) {
                        return (
                            !loadingPlans && <Button variant="contained" color="primary" type="submit">
                              Seleccionar este plan
                            </Button>
                          );
                    }
                    return null; // No renderiza ningún botón en otros casos
                 })()}
                </Box>

                {activeStep !== 0 && activeStep !== 1 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" component="span">
                      Usuario conectado:&nbsp;
                    </Typography>
                                  
                    <Typography variant="caption" component="span" sx={{ fontWeight: 'bold' }}>
                      {registrationUser?.email}
                    </Typography>
                  </Box>
                )}

            </form>

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
            </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'SignUp.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
    
};

export default SignUp;